import { DateTime } from 'luxon';
import omit from 'lodash/fp/omit';
import flow from 'lodash/fp/flow';
import update from 'lodash/fp/update';
import map from 'lodash/fp/map';
import { v4 as uuidv4 } from 'uuid';
import {
  mapIsoStringtoUtcObject,
  dateToMidnightUTCString
} from '~/modules/common/dates/convert';
import { ResourceRequestStatus, ResourceRequestAccessLevel } from '~/types';
import { RESOURCE_SUMMARY_COUNT_QUERY } from '~/modules/resourcing/common/hooks/useResourcePlanSummaryCounts';
import RESOURCE_REQUESTS_ALLOCATIONS_QUERY from '~/modules/resourcing/common/fragments/resourceRequestsQuery';
import resourceRequestQuery from '~/modules/resourcing/common/resourceRequestQuery';
import { makeTenantScopedUri } from '~/util';
import { updateResourcePlansForTenantCacheSingleRequest } from './cacheUpdates';

export const generateSequence = number => {
  const appliedArray = number >= 0 ? Array(number) : Array(1);

  return Array.apply(0, appliedArray).map((item, index) => index);
};

export const getResourceRequestDatesFromProjectDate = ({
  projectEndDate,
  projectStartDate,
  scale,
  outputDateTransformFn = date => mapIsoStringtoUtcObject(date)
}) => {
  const projEndDate = projectEndDate && mapIsoStringtoUtcObject(projectEndDate);

  const today = DateTime.utc();
  const requestStartDate = DateTime.max(
    ...(projectStartDate ? [mapIsoStringtoUtcObject(projectStartDate)] : []),
    today
  );
  const requestEndDateScale = scale
    ? scale === 'days'
      ? 'weeks'
      : scale
    : 'months';
  const requestEndDate =
    projEndDate && requestStartDate < projEndDate
      ? projEndDate
      : requestStartDate
          .plus({ [requestEndDateScale]: 1 })
          .minus({ days: scale ? 1 : 0 });

  return {
    requestStartDate: outputDateTransformFn(
      dateToMidnightUTCString(requestStartDate)
    ),
    requestEndDate: outputDateTransformFn(
      dateToMidnightUTCString(requestEndDate)
    )
  };
};

export const omitForbiddenFields = flow(
  omit('__typename'),
  omit('startDate'),
  omit('endDate'),
  omit('location'),
  omit('division'),
  omit('serviceCenter'),
  omit('costCenter'),
  omit('employeeType'),
  omit('department'),
  omit('requestStatus'),
  omit('role'),
  omit('resourceAllocations'),
  omit('currency'),
  omit('project'),
  omit('resourcePools'),
  update(
    'scheduleRules',
    map(flow(omit('__typename'), update('dateRange', flow(omit('__typename')))))
  ),
  update('requestAttributeWeights', omit('__typename')),
  omit('exchangeRateValues'),
  omit('totalRequestedCostByCurrency'),
  omit('preferredResources'),
  omit('sourceMetadata'),
  omit('isDelaDraftResourceRequest'),
  omit('resourceMatches'),
  omit('resourceMatchesStatus'),
  omit('mergedResourceRequests')
);

export const appendScheduleRuleTypeNameFields = rule => ({
  ...rule,
  __typename: 'ScheduleRule',
  dateRange: {
    ...rule.dateRange,
    __typename: 'DateRange'
  }
});

const getCurrency = baseCurrency => ({
  id: baseCurrency.id,
  displayText: baseCurrency.displayText,
  __typename: 'Currency'
});

export const getRequestRoleAttributesFromRoleChangeEvent = (
  event,
  baseCurrency
) =>
  event
    ? {
        role: {
          ...event,
          currentRate: event.currentRate || null
        },
        roleUri: event.id,
        roleRate: event.currentRate ? event.currentRate.amount : 0,
        currencyUri: event.currentRate
          ? event.currentRate.currency.id
          : baseCurrency.id,
        currency: event.currentRate
          ? event.currentRate.currency
          : getCurrency(baseCurrency)
      }
    : {
        role: null,
        roleUri: null,
        currencyUri: baseCurrency.id,
        currency: baseCurrency,
        roleRate: null,
        skills: []
      };

export const getDateRangeFromStartDateChangeEvent = (event, { start, end }) => {
  const startDateObject =
    event && DateTime.utc(event.year, event.month, event.day, 0, 0, 0, 0);

  const isGreaterThanEndDate =
    startDateObject && end && startDateObject > DateTime.fromISO(end);

  const startDate = startDateObject && startDateObject.toISO();
  const endDate = isGreaterThanEndDate ? startDate : end;

  return { startDate, endDate };
};

export const getDateRangeFromEndDateChangeEvent = (event, { start, end }) => {
  const endDateObject =
    event && DateTime.utc(event.year, event.month, event.day, 0, 0, 0, 0);

  const isLessThanStartDate =
    endDateObject && start && endDateObject < DateTime.fromISO(start);

  const endDate = endDateObject && endDateObject.toISO();
  const startDate = isLessThanStartDate ? endDate : start;

  return { startDate, endDate };
};

export const replaceProjectResourceRequest = (
  existingResourceRequests,
  newRequest
) =>
  existingResourceRequests.map(sr =>
    sr.id === newRequest.id ? newRequest : sr
  );

export const appendResourceRequestFields = resourceRequest => ({
  ...resourceRequest,
  roleUri: resourceRequest.role && resourceRequest.role.id,
  locationUri: resourceRequest.location && resourceRequest.location.id,
  divisionUri: resourceRequest.division && resourceRequest.division.id,
  costCenterUri: resourceRequest.costCenter && resourceRequest.costCenter.id,
  serviceCenterUri:
    resourceRequest.serviceCenter && resourceRequest.serviceCenter.id,
  departmentUri: resourceRequest.department && resourceRequest.department.id,
  employeeTypeUri:
    resourceRequest.employeeType && resourceRequest.employeeType.id,
  skills:
    resourceRequest.skills && resourceRequest.skills.length
      ? resourceRequest.skills.map(skill => ({
          skillUri: skill.id,
          skillLevelUri: skill.skillLevel.id
        }))
      : [],
  tags:
    resourceRequest.tags && resourceRequest.tags.length
      ? resourceRequest.tags.map(({ tag, value }) => ({
          uri: tag.id,
          value: value.id
        }))
      : [],
  resourcePoolUris: (resourceRequest.resourcePools || []).map(pool => pool.id),
  preferredResourceUris: (resourceRequest.preferredResources || []).map(
    r => r.id
  )
});

const tryLoadCachedQuery = (proxy, optimistic) => {
  try {
    return proxy.readQuery(
      {
        query: RESOURCE_SUMMARY_COUNT_QUERY,
        variables: {
          requestAccessLevel: ResourceRequestAccessLevel.ResourceManager
        }
      },
      optimistic
    );
  } catch (e) {
    return null;
  }
};

const tryLoadSummaryCountCachedQuery = ({ proxy, optimistic, variables }) => {
  try {
    return proxy.readQuery(
      {
        query: RESOURCE_SUMMARY_COUNT_QUERY,
        variables
      },
      optimistic
    );
  } catch (e) {
    return null;
  }
};

export const tryLoadCachedResourceRequestsQuery = ({
  proxy,
  projectUri,
  optimistic
}) => {
  try {
    return proxy.readQuery(
      {
        query: RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
        variables: {
          projectUri,
          limit: 500,
          cursor: null
        }
      },
      optimistic
    );
  } catch (e) {
    return null;
  }
};

export const tryLoadCachedResourceRequestQuery = ({
  proxy,
  resourceRequestId,
  optimistic
}) => {
  try {
    return proxy.readQuery(
      {
        query: resourceRequestQuery,
        variables: {
          id: resourceRequestId
        }
      },
      optimistic
    );
  } catch (e) {
    return null;
  }
};

export const calculateSummaryTabCount = ({
  cachedSummaryTabCount,
  existingRequestStatus,
  newRequestStatus
}) => {
  const {
    resourcePlansSummaryForCurrentTenant: {
      pendingCount,
      proposedCount,
      toBeHiredCount,
      allocationRejectedCount
    }
  } = cachedSummaryTabCount;

  const newSummaryTabCount = {
    pendingCount,
    proposedCount,
    toBeHiredCount,
    allocationRejectedCount
  };

  switch (existingRequestStatus) {
    case ResourceRequestStatus.Submitted:
      newSummaryTabCount.pendingCount -= 1;

      if (newRequestStatus === ResourceRequestStatus.Tobehired) {
        newSummaryTabCount.toBeHiredCount += 1;
      }

      break;
    case ResourceRequestStatus.Allocationrejected:
      newSummaryTabCount.allocationRejectedCount -= 1;

      if (newRequestStatus === ResourceRequestStatus.Tentative) {
        newSummaryTabCount.proposedCount += 1;
      }

      if (newRequestStatus === ResourceRequestStatus.Tobehired) {
        newSummaryTabCount.toBeHiredCount += 1;
      }

      break;
    case ResourceRequestStatus.Tentative:
      newSummaryTabCount.proposedCount -= 1;

      if (newRequestStatus === ResourceRequestStatus.Allocationrejected) {
        newSummaryTabCount.allocationRejectedCount += 1;
      }

      if (newRequestStatus === ResourceRequestStatus.Submitted) {
        newSummaryTabCount.pendingCount += 1;
      }

      break;
    case ResourceRequestStatus.Tobehired:
      if (existingRequestStatus === newRequestStatus) break;

      newSummaryTabCount.toBeHiredCount -= 1;

      if (newRequestStatus === ResourceRequestStatus.Submitted) {
        newSummaryTabCount.pendingCount += 1;
      }
      break;
    case ResourceRequestStatus.Rejected:
      if (newRequestStatus === ResourceRequestStatus.Tobehired) {
        newSummaryTabCount.toBeHiredCount += 1;
      }

      if (newRequestStatus === ResourceRequestStatus.Submitted) {
        newSummaryTabCount.pendingCount += 1;
      }

      break;
    case ResourceRequestStatus.Draft:
      if (newRequestStatus === ResourceRequestStatus.Tobehired) {
        newSummaryTabCount.toBeHiredCount += 1;
      }

      if (newRequestStatus === ResourceRequestStatus.Submitted) {
        newSummaryTabCount.pendingCount += 1;
      }

      break;
    default:
      break;
  }

  return newSummaryTabCount;
};

export const updateResourcePlanSummaryTabCount = ({
  proxy,
  isOptimistic,
  newRequestStatus,
  numberOfRequests = 1,
  existingRequestStatus,
  requestAccessLevel = ResourceRequestAccessLevel.All
}) => {
  if (
    !(
      newRequestStatus === ResourceRequestStatus.Tobehired ||
      newRequestStatus === ResourceRequestStatus.Rejected ||
      newRequestStatus === ResourceRequestStatus.Submitted ||
      newRequestStatus === ResourceRequestStatus.Tentative
    )
  )
    return;

  const cachedSummaryTabCount = tryLoadCachedQuery(proxy, isOptimistic);

  if (!cachedSummaryTabCount) return;

  const {
    resourcePlansSummaryForCurrentTenant: {
      pendingCount,
      proposedCount,
      toBeHiredCount
    }
  } = cachedSummaryTabCount;

  const newSummaryTabCounts =
    existingRequestStatus === ResourceRequestStatus.Tobehired &&
    newRequestStatus === ResourceRequestStatus.Rejected
      ? {
          ...cachedSummaryTabCount.resourcePlansSummaryForCurrentTenant,
          toBeHiredCount: toBeHiredCount - 1
        }
      : existingRequestStatus === ResourceRequestStatus.Submitted &&
        newRequestStatus === ResourceRequestStatus.Rejected
      ? {
          ...cachedSummaryTabCount.resourcePlansSummaryForCurrentTenant,
          pendingCount: pendingCount - 1
        }
      : existingRequestStatus === ResourceRequestStatus.Submitted &&
        newRequestStatus === ResourceRequestStatus.Tobehired
      ? {
          ...cachedSummaryTabCount.resourcePlansSummaryForCurrentTenant,
          pendingCount: pendingCount - 1,
          toBeHiredCount: toBeHiredCount + 1
        }
      : existingRequestStatus === ResourceRequestStatus.Submitted &&
        newRequestStatus === ResourceRequestStatus.Tentative
      ? {
          ...cachedSummaryTabCount.resourcePlansSummaryForCurrentTenant,
          pendingCount: pendingCount - numberOfRequests,
          proposedCount: proposedCount + numberOfRequests
        }
      : existingRequestStatus === ResourceRequestStatus.Rejected &&
        newRequestStatus === ResourceRequestStatus.Submitted
      ? {
          ...cachedSummaryTabCount.resourcePlansSummaryForCurrentTenant,
          pendingCount: pendingCount + 1
        }
      : existingRequestStatus === ResourceRequestStatus.Tentative &&
        newRequestStatus === ResourceRequestStatus.Submitted
      ? {
          ...cachedSummaryTabCount.resourcePlansSummaryForCurrentTenant,
          pendingCount: pendingCount + 1,
          proposedCount: proposedCount - 1
        }
      : existingRequestStatus === ResourceRequestStatus.Rejected &&
        newRequestStatus === ResourceRequestStatus.Tobehired
      ? {
          ...cachedSummaryTabCount.resourcePlansSummaryForCurrentTenant,
          toBeHiredCount: toBeHiredCount + 1
        }
      : existingRequestStatus === ResourceRequestStatus.Allocationrejected &&
        newRequestStatus === ResourceRequestStatus.Tentative
      ? {
          ...cachedSummaryTabCount.resourcePlansSummaryForCurrentTenant,
          pendingCount: pendingCount - 1,
          proposedCount: proposedCount + 1
        }
      : cachedSummaryTabCount.resourcePlansSummaryForCurrentTenant;

  proxy.writeQuery({
    query: RESOURCE_SUMMARY_COUNT_QUERY,
    variables: {
      requestAccessLevel
    },
    data: {
      resourcePlansSummaryForCurrentTenant: {
        ...cachedSummaryTabCount.resourcePlansSummaryForCurrentTenant,
        ...newSummaryTabCounts
      }
    }
  });
};

export const buildResourceRequest = ({
  project: {
    id,
    defaultScheduleRule,
    startDate: projectStartDate,
    endDate: projectEndDate
  },
  me,
  scale,
  resourceRequestDetails = {}
}) => {
  const {
    requestStartDate,
    requestEndDate
  } = getResourceRequestDatesFromProjectDate({
    projectStartDate,
    projectEndDate,
    scale
  });
  const requestScheduleRule = {
    ...defaultScheduleRule,
    dateRange: {
      startDate: dateToMidnightUTCString(requestStartDate),
      endDate: dateToMidnightUTCString(requestEndDate)
    }
  };

  return {
    id: makeTenantScopedUri(me, 'psa-resource-request', uuidv4()),
    projectUri: id,
    requestStatus: ResourceRequestStatus.Draft,
    scheduleRules: [requestScheduleRule],
    quantity: 1,
    load: 100,
    roleRate: 0,
    currencyUri: null,
    ...appendResourceRequestFields(resourceRequestDetails)
  };
};

export const updateResourcePlansCache = ({
  existingResourceRequest,
  newRequestStatus,
  requestAccessLevel = ResourceRequestAccessLevel.All,
  planFilter,
  chartSelectedTab
}) => proxy => {
  const cacheVariables = {
    paginationToken: null,
    requestAccessLevel,
    filter: {
      ...(planFilter ? { ...planFilter } : {}),
      planStatus: chartSelectedTab
    },
    groupBy: 'PROJECT'
  };

  updateResourcePlansForTenantCacheSingleRequest({
    proxy,
    existingResourceRequest,
    newRequestStatus,
    requestAccessLevel,
    cacheVariables
  });
};

export const updateResourcePlanSummaryTabCountCache = ({
  responseType,
  newRequestStatus,
  resourceRequest,
  requestAccessLevel = ResourceRequestAccessLevel.All,
  planFilter
}) => (proxy, mutationResponse) => {
  const {
    data: {
      [responseType]: { isOptimistic }
    }
  } = mutationResponse;

  const cachedSummaryTabCount = tryLoadSummaryCountCachedQuery({
    proxy,
    isOptimistic,
    variables: {
      requestAccessLevel,
      ...(planFilter ? { filter: planFilter } : {})
    }
  });

  if (!cachedSummaryTabCount) return;

  const newSummaryTabCounts = calculateSummaryTabCount({
    cachedSummaryTabCount,
    existingRequestStatus: resourceRequest.requestStatus,
    newRequestStatus
  });

  proxy.writeQuery({
    query: RESOURCE_SUMMARY_COUNT_QUERY,
    variables: {
      requestAccessLevel,
      ...(planFilter ? { filter: planFilter } : {})
    },
    data: {
      resourcePlansSummaryForCurrentTenant: {
        ...cachedSummaryTabCount.resourcePlansSummaryForCurrentTenant,
        ...newSummaryTabCounts
      }
    }
  });
};

export const bulkUpdateResourcePlanSummaryTabCountCache = ({
  responseType,
  newRequestStatus,
  oldRequestStatus,
  numberOfRequests,
  requestAccessLevel = ResourceRequestAccessLevel.All
}) => (proxy, mutationResponse) => {
  const {
    data: {
      [responseType]: { isOptimistic }
    }
  } = mutationResponse;

  updateResourcePlanSummaryTabCount({
    proxy,
    isOptimistic,
    requestAccessLevel,
    existingRequestStatus: oldRequestStatus,
    newRequestStatus,
    numberOfRequests
  });
};

export const updateResourceRequestsCache = ({ projectId, responseType }) => (
  proxy,
  mutationResponse
) => {
  const {
    data: {
      [responseType]: { isOptimistic, resourceRequest: newRequest }
    }
  } = mutationResponse;

  const cachedResourceRequests = tryLoadCachedResourceRequestsQuery({
    proxy,
    projectUri: projectId,
    optimistic: isOptimistic
  });

  if (cachedResourceRequests) {
    const { resourceRequests: data } = cachedResourceRequests;

    proxy.writeQuery({
      query: RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
      variables: {
        projectUri: projectId,
        cursor: null
      },
      data: {
        resourceRequests: {
          ...data,
          resourceRequests: replaceProjectResourceRequest(
            data.resourceRequests,
            newRequest
          )
        }
      }
    });
  }
};

export const updateCache = (
  updateFunction,
  projectId,
  responseType,
  newRequestStatus,
  existingResourceRequest,
  chartSelectedTab,
  requestAccessLevel = ResourceRequestAccessLevel.All
) => (proxy, mutationResponse) => {
  const {
    data: {
      [responseType]: { isOptimistic, resourceRequest: newRequest }
    }
  } = mutationResponse;

  updateResourcePlanSummaryTabCount({
    proxy,
    isOptimistic,
    newRequestStatus,
    existingRequestStatus: existingResourceRequest.requestStatus,
    requestAccessLevel
  });

  const cachedResourceRequests = tryLoadCachedResourceRequestsQuery({
    proxy,
    projectUri: projectId,
    optimistic: isOptimistic
  });

  if (cachedResourceRequests) {
    const { resourceRequests: data } = cachedResourceRequests;

    proxy.writeQuery({
      query: RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
      variables: {
        projectUri: projectId,
        cursor: null
      },
      data: {
        resourceRequests: {
          ...data,
          resourceRequests: updateFunction(data.resourceRequests, newRequest)
        }
      }
    });
  }

  if (chartSelectedTab) {
    updateResourcePlansForTenantCacheSingleRequest({
      proxy,
      existingResourceRequest,
      newRequestStatus,
      chartSelectedTab,
      requestAccessLevel
    });
  }
};

const cacheUpdateFunctionsCalls = cacheUpdateFunctions => (
  proxy,
  mutationResponse
) =>
  (cacheUpdateFunctions || []).forEach(func => func(proxy, mutationResponse));

export const mapToServiceRequest = ({
  request,
  responseType,
  responseTypeName,
  requestStatus,
  cacheUpdate: legacyCacheUpdateCallback,
  cacheUpdateFunctions,
  sourceMetadata,
  chartSelectedTab,
  requestAccessLevel = ResourceRequestAccessLevel.All,
  refetchQueries = [],
  isPsaPraaResourceMatchingEnabled = false,
  resourceMatchesStatus
}) => {
  return {
    variables: {
      notifyOnNetworkStatusChange: true,
      input: {
        resourceRequestId: request.id
      },
      isPsaPraaResourceMatchingEnabled
    },
    context: {
      debounceKey: `${request.id}_status`,
      debounceTimeout: 100
    },
    refetchQueries,
    optimisticResponse: {
      __typename: 'Mutation',
      [responseType]: {
        __typename: responseTypeName,
        isOptimistic: true,
        resourceRequest: {
          __typename: 'ResourceRequest',
          ...request,
          requestStatus,
          sourceMetadata: sourceMetadata || request.sourceMetadata,
          resourceMatchesStatus:
            resourceMatchesStatus || request.resourceMatchesStatus
        }
      }
    },
    update: cacheUpdateFunctions?.length
      ? cacheUpdateFunctionsCalls(cacheUpdateFunctions)
      : legacyCacheUpdateCallback
      ? legacyCacheUpdateCallback(
          replaceProjectResourceRequest,
          request.projectUri,
          responseType,
          requestStatus,
          request
        )
      : updateCache(
          replaceProjectResourceRequest,
          request.projectUri,
          responseType,
          requestStatus,
          request,
          chartSelectedTab,
          requestAccessLevel
        )
  };
};

export const checkIfResourceRequestIsReadOnly = resourceRequest =>
  Boolean(
    resourceRequest.mergedResourceRequests?.length > 1 ||
      (resourceRequest.requestStatus !== ResourceRequestStatus.Draft &&
        resourceRequest.requestStatus !== ResourceRequestStatus.Rejected)
  );

export const mapToServiceRequestWithTimeoff = ({
  request,
  responseType,
  responseTypeName,
  requestStatus,
  cacheUpdate: legacyCacheUpdateCallback,
  chartSelectedTab,
  requestAccessLevel = ResourceRequestAccessLevel.All,
  showTimeOff,
  chartDateRange,
  showHolidays,
  refetchQueries = [],
  cacheUpdateFunctions
}) => {
  const requestRole = request.role
    ? {
        __typename: request.role.__typename,
        displayText: request.role.displayText,
        id: request.role.id,
        uri: request.role.id
      }
    : {};

  const allocationsWithTimeoffs = request.resourceAllocations.map(
    allocation => ({
      ...allocation,
      user: {
        ...allocation.user,
        timeoffs: allocation.user.timeoffs || [],
        holidays: allocation.user.holidays || []
      },
      role: allocation.role || requestRole
    })
  );

  const requestWithTimeoffs = {
    ...request,
    resourceAllocations: allocationsWithTimeoffs
  };

  return {
    variables: {
      notifyOnNetworkStatusChange: true,
      input: {
        resourceRequestId: requestWithTimeoffs.id
      },
      chartDateRange,
      showTimeOff,
      showHolidays
    },
    context: {
      debounceKey: `${requestWithTimeoffs.id}_status`,
      debounceTimeout: 100
    },
    optimisticResponse: {
      __typename: 'Mutation',
      [responseType]: {
        __typename: responseTypeName,
        isOptimistic: true,
        resourceRequest: {
          __typename: 'ResourceRequest',
          ...requestWithTimeoffs,
          requestStatus
        }
      }
    },
    refetchQueries,
    update: cacheUpdateFunctions?.length
      ? cacheUpdateFunctionsCalls(cacheUpdateFunctions)
      : legacyCacheUpdateCallback
      ? legacyCacheUpdateCallback(
          replaceProjectResourceRequest,
          request.projectUri,
          responseType,
          requestStatus,
          request
        )
      : updateCache(
          replaceProjectResourceRequest,
          request.projectUri,
          responseType,
          requestStatus,
          request,
          chartSelectedTab,
          requestAccessLevel
        )
  };
};

export const normalizeFilter = filter => {
  const { roleUris, searchText, userIds, resourcePoolIds } = filter;

  if (roleUris || searchText || userIds || resourcePoolIds) return filter;

  const { roles, text, users, resourcePools } = filter;

  if (!roles && !text && !users && !resourcePools) {
    return {};
  }

  return {
    searchText: text,
    roleUris: roles,
    userIds: users,
    resourcePoolIds: resourcePools
  };
};
